import { MoreHorizontal, Download, Pencil, Trash2, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/modal';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface ArtifactOverflowMenuProps {
  onDownload: () => void;
  isDownloading?: boolean;
  canRename: boolean;
  onRename: () => void;
  canShare: boolean;
  onShare: () => void;
  canDelete: boolean;
  onDelete: () => void;
  /** Extra classes on the trigger button */
  triggerClassName?: string;
}

export function ArtifactOverflowMenu({
  onDownload,
  isDownloading = false,
  canRename,
  onRename,
  canShare,
  onShare,
  canDelete,
  onDelete,
  triggerClassName = 'h-7 w-7 shrink-0',
}: ArtifactOverflowMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          className={triggerClassName}
          aria-label='More actions'
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className='h-4 w-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem
          disabled={isDownloading}
          onClick={(e) => {
            e.stopPropagation();
            void onDownload();
          }}
        >
          <Download className='size-4' />
          {isDownloading ? 'Downloading…' : 'Download'}
        </DropdownMenuItem>
        {canRename && (
          <DropdownMenuItem onClick={onRename}>
            <Pencil className='size-4' />
            Rename
          </DropdownMenuItem>
        )}
        {canShare && (
          <DropdownMenuItem onClick={onShare}>
            <Share2 className='size-4' />
            Share
          </DropdownMenuItem>
        )}
        {canDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDelete}>
              <Trash2 className='size-4' />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface RenameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onChange: (value: string) => void;
  onConfirm: () => void;
  isPending?: boolean;
}

export function ArtifactRenameDialog({
  open,
  onOpenChange,
  value,
  onChange,
  onConfirm,
  isPending,
}: RenameDialogProps) {
  return (
    <Modal
      title='Rename artifact'
      isDialogOpen={open}
      setIsDialogOpen={onOpenChange}
      footer={
        <>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => void onConfirm()} disabled={isPending}>
            Rename
          </Button>
        </>
      }
    >
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && void onConfirm()}
        autoFocus
      />
    </Modal>
  );
}

interface DeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  onConfirm: () => void;
  isPending?: boolean;
}

export function ArtifactDeleteDialog({
  open,
  onOpenChange,
  title,
  onConfirm,
  isPending,
}: DeleteDialogProps) {
  return (
    <Modal
      title='Delete artifact?'
      isDialogOpen={open}
      setIsDialogOpen={onOpenChange}
      footer={
        <>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant='destructive'
            onClick={() => void onConfirm()}
            disabled={isPending}
          >
            Delete
          </Button>
        </>
      }
    >
      <p className='text-sm text-muted-foreground'>
        &ldquo;{title}&rdquo; will be permanently deleted. This cannot be
        undone.
      </p>
    </Modal>
  );
}
